import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import api from "../adapter/base";
import TimeCal from "../common/timecal";
import {
    Card,
    Pagination,
    List,
    Avatar,
    Modal,
    Input,
    message,
    Space,
    Switch,
    Upload,
    Image,
    Button,
    Spin,
    Row,
    Col,
    Typography,
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    UploadOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Search } = Input;

const SupplierManagement = () => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    });


    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm]);

    const [visible, setVisible] = useState(false);
    const [deletevisible, setDeletevisible] = useState(false);
    const [addVisible, setAddVisible] = useState(false);
    const [supplier, setSupplier] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const {
        data: suppliersData,
        isLoading
    } = useQuery(
        ["all_suppliers", page, debouncedSearchTerm],
        () =>
            api.get("suppliers", {
                params: {
                    page,
                    name: debouncedSearchTerm || undefined,
                },
            }),
        {
            keepPreviousData: true,
            onSuccess: (response) => {
                if (response?.data?.data) {
                    setData(response.data.data);
                }
                if (response?.data?.meta) {
                    setMeta(response.data.meta);
                }
            },
        }
    );

    const resetImageStates = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    const openEditModal = (item) => {
        setSupplier({ ...item, address: item.address || {} });
        resetImageStates();
        setVisible(true);
    };

    const closeEditModal = () => {
        setVisible(false);
        setSupplier(null);
        resetImageStates();
    };

    const openAddModal = () => {
        setAddVisible(true);
        setSupplier({
            is_active: true,
            address: {},
            name: "",
            name_am: "",
            phone_number: "",
        });
        resetImageStates();
    };

    const closeAddModal = () => {
        setAddVisible(false);
        setSupplier(null);
        resetImageStates();
    };

    const openDeleteModal = (item) => {
        setSupplier(item);
        setDeletevisible(true);
    };

    const closeDeleteModal = () => {
        setDeletevisible(false);
        setSupplier(null);
    };

    const supplierToFormData = (supplierData, includeImage = true, update = true) => {
        const formData = new FormData();

        if (supplierData.name) formData.append("name", supplierData.name);
        if (supplierData.name_am) formData.append("name_am", supplierData.name_am);
        if (supplierData.phone_number) formData.append("phone_number", supplierData.phone_number);
        formData.append("is_active", supplierData.is_active ? 1 : 0);

        if (update) formData.append("_method", "PUT");

        if (supplierData.address) {
            if (supplierData.address.city) formData.append("address[city]", supplierData.address.city);
            if (supplierData.address.sub_city) formData.append("address[sub_city]", supplierData.address.sub_city);
            if (supplierData.address.woreda) formData.append("address[woreda]", supplierData.address.woreda);
            if (supplierData.address.neighborhood) formData.append("address[neighborhood]", supplierData.address.neighborhood);
            if (supplierData.address.house_number) formData.append("address[house_number]", supplierData.address.house_number);
        }

        if (includeImage && imageFile) {
            formData.append("image", imageFile);
        }

        return formData;
    };

    const updateSupplier = async () => {
        if (!supplier) return;
        try {
            const formData = supplierToFormData(supplier, !!imageFile, true);
            const response=await api.post(`suppliers/${supplier.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            message.success("Updated successfully");

            if (response?.data?.data) {
                const updatedSupplier = response.data.data;

                setData(prevData =>
                    prevData.map(item =>
                        item.id === updatedSupplier.id ? updatedSupplier : item
                    )
                );
            }
            // await queryClient.refetchQueries(["all_suppliers", page, debouncedSearchTerm]);
            await queryClient.invalidateQueries(["all_suppliers", page, debouncedSearchTerm]);
            closeEditModal();
        } catch (err) {
            message.error(err?.response?.data?.message || "Update failed");
        }
    };

    const registerSupplier = async () => {
        if (!supplier) return;
        try {
            const formData = supplierToFormData(supplier, !!imageFile, false);
            await api.post("suppliers", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            message.success("Registered successfully");
            setPage(1);
            await queryClient.invalidateQueries(["all_suppliers", 1, debouncedSearchTerm]);
            closeAddModal();
        } catch (err) {
            console.log(err);
            message.warning(err?.response?.data?.message || "Registration failed");
        }
    };

    const deleteSupplier = async () => {
        if (!supplier) return;
        try {
            await api.delete(`/suppliers/${supplier.id}`);
            message.success("Deleted Successfully");
            if (data.length === 1 && page > 1) {
                setPage(page - 1);
                queryClient.invalidateQueries(["all_suppliers", page - 1, debouncedSearchTerm]);
            } else {
                queryClient.invalidateQueries(["all_suppliers", page, debouncedSearchTerm]);
            }
            closeDeleteModal();
        } catch (err) {
            message.error(err?.response?.data?.message);
        }
    };

    const toggleStatus = async (item, checked) => {
        try {
            const formData = new FormData();
            formData.append("is_active", checked ? 1 : 0);
            formData.append("_method", "PUT");
            await api.post(`suppliers/${item.id}`, formData);
            message.success("Status updated successfully");
            queryClient.invalidateQueries(["all_suppliers", page, debouncedSearchTerm]);
        } catch (err) {
            message.error(err?.response?.data?.message || "Update failed");
        }
    };

    const sortLocal = () => {
        const sortedData = [...data].sort((a, b) => a.name?.localeCompare(b.name));
        setData(sortedData);
    };

    const handleImageChange = (file) => {
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
        return false;
    };

    useEffect(() => {
        if (suppliersData?.data?.data) {
            setData(suppliersData.data.data);
        }
    }, [suppliersData]);

    return (
        <div className="px-32 py-10" style={{ background: "#f5f5f5", minHeight: "100vh" }}>
            <Card
                style={{
                    borderRadius: 16,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    border: "none",
                }}
                bodyStyle={{ padding: "50px" }}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Text strong style={{ fontSize: 20, color: "#252733" }}>
                            Suppliers
                        </Text>
                    </Col>
                    <Col>
                        <Space size="large">
                            <Button type="text" icon={<i className="fa-solid fa-sort" />} onClick={sortLocal}>
                                Sort
                            </Button>
                            <Button
                                type="secondary"
                                icon={<PlusOutlined />}
                                onClick={openAddModal}
                                style={{ borderRadius: 8 }}
                            >
                                Add
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Row style={{ marginBottom: 24 }}>
                    <Col span={24}>
                        <Search
                            placeholder="Search by supplier name "
                            allowClear
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onSearch={(value) => setSearchTerm(value)}
                            size="middle"
                        />
                    </Col>
                </Row>

                <Row
                    style={{
                        padding: "12px 16px",
                        background: "#f9f9fc",
                        borderRadius: 12,
                        marginBottom: 8,
                        fontWeight: 500,
                        color: "#8c8c8c",
                    }}
                >
                    <Col flex="2.5">
                        <Text type="secondary">Supplier Name</Text>
                    </Col>
                    <Col flex="1" style={{ textAlign: "center" }}>
                        <Text type="secondary">Status</Text>
                    </Col>
                    <Col flex="0.8" style={{ textAlign: "center" }}>
                        <Text type="secondary">Edit</Text>
                    </Col>
                    <Col flex="0.8" style={{ textAlign: "center" }}>
                        <Text type="secondary">Delete</Text>
                    </Col>
                </Row>

                <div style={{ borderBottom: "1px solid #f0f0f0", marginBottom: 16 }} />

                <Spin spinning={isLoading}>
                    <List
                        dataSource={data}
                        locale={{ emptyText: "No Suppliers found" }}
                        renderItem={(item) => (
                            <List.Item style={{ padding: "12px 0", borderBottom: "none" }}>
                                <Row align="middle" style={{ width: "100%" }}>
                                    <Col flex="2.5">
                                        <Space size="middle">
                                            <Avatar
                                                src={item.image_path}
                                                size={44}
                                                shape="circle"
                                            />
                                            <div>
                                                <Text strong style={{ display: "block" }}>
                                                    {item.name}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {item.created_at ? TimeCal(item.created_at) : "date not registered"}
                                                </Text>
                                            </div>
                                        </Space>
                                    </Col>
                                    <Col flex="1" style={{ textAlign: "center" }}>
                                        <Switch
                                            checked={item.is_active === 1 || item.is_active === true}
                                            onChange={(checked) => toggleStatus(item, checked)}
                                        />
                                    </Col>
                                    <Col flex="0.8" style={{ textAlign: "center" }}>
                                        <Button
                                            type="link"
                                            icon={<EditOutlined style={{ color: "#F4AD33", fontSize: 18 }} />}
                                            onClick={() => openEditModal(item)}
                                        />
                                    </Col>
                                    <Col flex="0.8" style={{ textAlign: "center" }}>
                                        <Button
                                            type="link"
                                            icon={<DeleteOutlined style={{ color: "#F4AD33", fontSize: 18 }} />}
                                            onClick={() => openDeleteModal(item)}
                                        />
                                    </Col>
                                </Row>
                            </List.Item>
                        )}
                    />
                </Spin>

                <div style={{ borderBottom: "1px solid #f0f0f0", margin: "16px 0" }} />

                <Row justify="end" align="middle" style={{ marginTop: 16 }}>
                    <Col>
                        <Text type="secondary" style={{ marginRight: 16 }}>
                            Total {meta.total} Suppliers
                        </Text>
                        <Pagination
                            current={meta.current_page}
                            total={meta.total}
                            pageSize={meta.per_page}
                            onChange={(newPage) => setPage(newPage)}
                            showSizeChanger={false}
                            showQuickJumper
                            style={{ color: "#BFBFBF" }}
                        />
                    </Col>
                </Row>
            </Card>

            <Modal
                title="Edit Supplier"
                open={visible}
                onOk={updateSupplier}
                onCancel={closeEditModal}
                okText="Update"
                cancelText="Cancel"
                width={600}
                destroyOnClose
                style={{ borderRadius: 16 }}
            >
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        {imagePreview || supplier?.image_path ? (
                            <Image
                                src={imagePreview || supplier?.image_path}
                                width={100}
                                height={100}
                                style={{ objectFit: "cover", borderRadius: 12 }}
                                preview={false}
                            />
                        ) : (
                            <div
                                style={{
                                    width: 100,
                                    height: 100,
                                    background: "#f5f5f5",
                                    borderRadius: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Text type="secondary">No Image</Text>
                            </div>
                        )}
                    </div>
                    <Upload beforeUpload={handleImageChange} showUploadList={false} accept="image/*">
                        <Button icon={<UploadOutlined />}>Upload New Image</Button>
                    </Upload>
                    <Input
                        addonBefore="Name"
                        value={supplier?.name}
                        onChange={(e) => setSupplier({ ...supplier, name: e.target.value })}
                    />
                    <Input
                        addonBefore="Name (Amharic)"
                        value={supplier?.name_am}
                        onChange={(e) => setSupplier({ ...supplier, name_am: e.target.value })}
                    />
                    <Input
                        addonBefore="Phone"
                        value={supplier?.phone_number}
                        onChange={(e) => setSupplier({ ...supplier, phone_number: e.target.value })}
                    />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Active Status:</span>
                        <Switch
                            checked={supplier?.is_active === 1 || supplier?.is_active === true}
                            onChange={(checked) => setSupplier({ ...supplier, is_active: checked })}
                        />
                    </div>
                    <Input
                        addonBefore="City"
                        value={supplier?.address?.city}
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier.address, city: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Sub City"
                        value={supplier?.address?.sub_city}
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier.address, sub_city: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Woreda"
                        value={supplier?.address?.woreda}
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier.address, woreda: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Neighborhood"
                        value={supplier?.address?.neighborhood}
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier.address, neighborhood: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="House Number"
                        value={supplier?.address?.house_number}
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier.address, house_number: e.target.value },
                            })
                        }
                    />
                </Space>
            </Modal>

            <Modal
                title="Add Supplier"
                open={addVisible}
                onOk={registerSupplier}
                onCancel={closeAddModal}
                okText="Create"
                cancelText="Cancel"
                width={600}
                destroyOnClose
                style={{ borderRadius: 16 }}
            >
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    {imagePreview && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <Image
                                src={imagePreview}
                                width={100}
                                height={100}
                                style={{ objectFit: "cover", borderRadius: 12 }}
                                preview={false}
                            />
                        </div>
                    )}
                    <Upload beforeUpload={handleImageChange} showUploadList={false} accept="image/*">
                        <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    <Input
                        addonBefore="Name"
                        onChange={(e) => setSupplier((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    <Input
                        addonBefore="Name (Amharic)"
                        onChange={(e) => setSupplier((prev) => ({ ...prev, name_am: e.target.value }))}
                    />
                    <Input
                        addonBefore="Phone"
                        placeholder="2519..."
                        maxLength={12}
                        showCount
                        onChange={(e) => setSupplier((prev) => ({ ...prev, phone_number: e.target.value }))}
                    />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Active Status:</span>
                        <Switch
                            defaultChecked={true}
                            onChange={(checked) => setSupplier((prev) => ({ ...prev, is_active: checked }))}
                        />
                    </div>
                    <Input
                        addonBefore="City"
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier?.address, city: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Sub City"
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier?.address, sub_city: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Woreda"
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier?.address, woreda: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="Neighborhood"
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier?.address, neighborhood: e.target.value },
                            })
                        }
                    />
                    <Input
                        addonBefore="House Number"
                        onChange={(e) =>
                            setSupplier({
                                ...supplier,
                                address: { ...supplier?.address, house_number: e.target.value },
                            })
                        }
                    />
                </Space>
            </Modal>

            <Modal
                title="Confirm Deletion"
                open={deletevisible}
                onOk={deleteSupplier}
                onCancel={closeDeleteModal}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                style={{ borderRadius: 16 }}
            >
                <p>
                    Are you sure you want to delete{" "}
                    <Text strong style={{ color: "#FAAD14" }}>
                        {supplier?.name}
                    </Text>{" "}
                    account? This action cannot be undone.
                </p>
            </Modal>
        </div>
    );
};

export default SupplierManagement;